import Icon from "@/components/Icon";
import { usePlatformDetection, type Platform } from "@/hooks/usePlatformDetection";
import classNames from "classnames";

export type KeyboardShortcutHintProps = {
  /** Key shown after ⌘ or Ctrl+ (e.g. `k`, `i`, `/`), or alone when `showModifier` is false. */
  keyLabel: string;
  className?: string;
  testId?: string;
  /** When false, show only the key label (single-key chords such as `d` or `g`). */
  showModifier?: boolean;
  /**
   * When true, non-Mac hints include Shift (e.g. Ctrl+Shift+P). Mac shows ⇧ before letter
   * keys (`⌘⇧P`). `keyLabel="?"` stays ⌘? on Mac (the help catalog).
   */
  implicitShift?: boolean;
  /** Light key caps for dark toolbar backgrounds (e.g. when a hint sits on a tinted control without its own surface). */
  tone?: "default" | "contrast";
  /** Smaller key cap for overlays that must not widen their host (e.g. nav tabs in go-nav mode). */
  size?: "default" | "compact";
  /** Force display of specific platform keyboard shortcut (for testing/stories only). */
  forcePlatform?: Platform;
};

export default function KeyboardShortcutHint({
  keyLabel,
  className,
  testId,
  showModifier = true,
  implicitShift = false,
  tone = "default",
  size = "default",
  forcePlatform
}: KeyboardShortcutHintProps) {
  const { isMac } = usePlatformDetection({ forcePlatform });

  const windowsModifierLabel = implicitShift ? `Ctrl+Shift+${keyLabel}` : `Ctrl+${keyLabel}`;
  const isContrast = tone === "contrast";
  const textClass = isContrast ? "text-extended-pgasilver-brand-on-color" : "text-sys-brand-on-surface-variant";
  const borderClass = isContrast ? "border-white/35" : "border-sys-brand-outline";
  const isCompact = size === "compact";
  /** Fixed square sized to fit a wide glyph like `w` so `i`, `n`, and `t` share the same box. */
  const isSingleKeyCap = !showModifier && keyLabel.length === 1;

  return (
    <div
      className={classNames(
        "rounded border shrink-0 flex items-center justify-center box-border",
        borderClass,
        isSingleKeyCap
          ? isCompact
            ? "h-4 w-4 px-0"
            : "h-5 w-5 px-0"
          : isCompact
            ? "h-4 min-w-4 px-0.5"
            : "px-1.5 py-0.5",
        className
      )}
      aria-hidden="true"
      data-testid={testId}
    >
      <div className="flex flex-row gap-0.5 items-center justify-center">
        {showModifier ? (
          isMac ? (
            <>
              <Icon name="keyboard_command_key" size={isCompact ? 10 : 12} className={textClass} />
              {implicitShift && keyLabel !== "?" ? (
                <span
                  className={classNames(isCompact ? "text-[10px] leading-3" : "text-label-small leading-4", textClass)}
                >
                  ⇧
                </span>
              ) : null}
              <span
                className={classNames(isCompact ? "text-[10px] leading-3" : "text-label-small leading-4", textClass)}
              >
                {keyLabel}
              </span>
            </>
          ) : (
            <span className={classNames(isCompact ? "text-[10px] leading-3" : "text-label-small leading-4", textClass)}>
              {windowsModifierLabel}
            </span>
          )
        ) : (
          <span
            className={classNames(
              isCompact ? "text-[10px] leading-3" : "text-label-small leading-4",
              isSingleKeyCap && "w-full text-center",
              textClass
            )}
          >
            {keyLabel}
          </span>
        )}
      </div>
    </div>
  );
}
